import React from "react";
import { useTranslation } from "react-i18next";
import { Handshake, Sprout, Sparkles } from "lucide-react";
import { ExternalLinkConfirm } from "@/components/result/ExternalLinkConfirm";
import { buildPlantLinks } from "@/lib/plantLinks";

/**
 * Result-page "Companion plants" suggestion panel.
 *
 * Gemini returns 2-3 species that pair well with the identified plant
 * (see `companion_plants` on `PlantIdentification`). Each row shows:
 *   - Common (+ scientific) name
 *   - One-sentence "why it pairs well" reason
 *   - A native pill when the species is native to the user's state
 *   - Up to 2 Learn-More pills (Wikipedia + state-specific fallback)
 *     gated by the shared ExternalLinkConfirm dialog.
 *
 * Doesn't render when the list is empty (legacy scans identified before
 * this field existed, or a plant Gemini couldn't name confidently).
 */
export const CompanionPlantsCard = ({ items = [], state }) => {
  const { t } = useTranslation();
  const list = Array.isArray(items) ? items.filter((it) => it && it.common_name) : [];
  if (!list.length) return null;

  return (
    <section
      data-testid="companion-plants-card"
      className="rounded-2xl bg-white border border-[#EFEBE3] p-4 md:p-5 shadow-botanical"
    >
      <div className="flex items-center gap-2 text-leaf-mist text-[11px] uppercase tracking-[0.14em]">
        <Handshake className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
        {t("companionPlants.title")}
      </div>
      <p className="mt-1 text-xs text-leaf-mist leading-snug">{t("companionPlants.subtitle")}</p>

      <ul className="mt-3 space-y-3" data-testid="companion-plants-list">
        {list.slice(0, 3).map((c, i) => {
          const learnLinks = buildPlantLinks({
            identification: {
              common_name: c.common_name,
              scientific_name: c.scientific_name,
            },
            state,
          }).slice(0, 2); // Wikipedia + state-specific (max) — keep panel tidy
          return (
            <li
              key={`${c.common_name}-${i}`}
              data-testid={`companion-plant-${i}`}
              className="rounded-xl border border-[#EFEBE3] bg-parchment/40 p-3"
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Sprout className="w-3.5 h-3.5 text-leaf shrink-0" strokeWidth={1.5} aria-hidden="true" />
                    <h4
                      className="text-sm font-medium text-leaf-ink"
                      data-testid={`companion-plant-${i}-name`}
                    >
                      {c.common_name}
                    </h4>
                    {c.is_state_native === true && (
                      <span
                        data-testid={`companion-plant-${i}-native-badge`}
                        className="inline-flex items-center gap-1 rounded-full bg-[#E6EFE9] text-leaf-deep text-[10px] uppercase tracking-wider px-2 py-0.5"
                      >
                        <Sparkles className="w-3 h-3" strokeWidth={1.5} aria-hidden="true" />
                        {t("companionPlants.nativeBadge", { state: state || "" }).trim()}
                      </span>
                    )}
                  </div>
                  {c.scientific_name && (
                    <p className="text-[11px] italic text-leaf-mist mt-0.5">
                      {c.scientific_name}
                    </p>
                  )}
                </div>
              </div>
              {c.reason && (
                <p className="mt-2 text-sm text-leaf-ink/85 leading-snug">{c.reason}</p>
              )}
              {learnLinks.length > 0 && (
                <div
                  className="mt-2 flex flex-wrap gap-1.5"
                  data-testid={`companion-plant-${i}-links`}
                >
                  {learnLinks.map((lnk) => (
                    <ExternalLinkConfirm
                      key={lnk.key}
                      href={lnk.url}
                      label={lnk.label}
                      source={lnk.label}
                      testId={`companion-plant-${i}-link-${lnk.key}`}
                      plantName={c.common_name}
                    />
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default CompanionPlantsCard;
